package com.bulut.mecitogullari.ui.screens.profile

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.bulut.mecitogullari.core.session.SessionManager
import com.bulut.mecitogullari.core.util.currentYearMonth
import com.bulut.mecitogullari.data.model.UnitStatus
import com.bulut.mecitogullari.data.model.User
import com.bulut.mecitogullari.data.model.computeMonthSummary
import com.bulut.mecitogullari.data.repository.AuthRepository
import com.bulut.mecitogullari.data.repository.ProfileRepository
import com.bulut.mecitogullari.data.repository.ProjectRepository
import com.bulut.mecitogullari.data.repository.TransactionRepository
import com.bulut.mecitogullari.data.repository.UnitRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class ProfileUiState(
    val user: User? = null,
    val companyName: String = "",
    val companySaved: Boolean = false,
    val projectCount: Int = 0,
    val soldUnits: Int = 0,
    val totalUnits: Int = 0,
    val currentMonthNet: Double = 0.0,
)

class ProfileViewModel(
    private val authRepository: AuthRepository,
    private val profileRepository: ProfileRepository,
    projectRepository: ProjectRepository,
    unitRepository: UnitRepository,
    transactionRepository: TransactionRepository,
) : ViewModel() {

    /** Local edit buffer: null = show the stored value; second = "just saved". */
    private val companyDraft = MutableStateFlow<Pair<String?, Boolean>>(null to false)

    private val stats = combine(
        projectRepository.projects,
        unitRepository.units,
        transactionRepository.transactions,
    ) { projects, units, transactions ->
        Triple(
            projects.size,
            units.count { it.status == UnitStatus.SOLD } to units.size,
            computeMonthSummary(transactions, currentYearMonth()).netProfit,
        )
    }

    val uiState: StateFlow<ProfileUiState> = combine(
        SessionManager.currentUser,
        profileRepository.companyName,
        companyDraft,
        stats,
    ) { user, storedCompany, (draft, saved), (projectCount, unitCounts, monthNet) ->
        ProfileUiState(
            user = user,
            companyName = draft ?: storedCompany,
            companySaved = saved,
            projectCount = projectCount,
            soldUnits = unitCounts.first,
            totalUnits = unitCounts.second,
            currentMonthNet = monthNet,
        )
    }.stateIn(
        scope = viewModelScope,
        started = SharingStarted.WhileSubscribed(5_000),
        initialValue = ProfileUiState(user = SessionManager.currentUser.value),
    )

    fun onCompanyNameChange(value: String) = companyDraft.update { value to false }

    fun saveCompanyName() {
        val name = (companyDraft.value.first ?: return).trim()
        viewModelScope.launch {
            profileRepository.setCompanyName(name)
            companyDraft.update { name to true }
        }
    }

    fun logout() {
        viewModelScope.launch {
            authRepository.logout()
        }
    }
}
